import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Send, CheckCircle } from 'lucide-react';
import { RewardHistoryItem } from '../types/RewardHistory';
import DefaultAvatar from '../../../components/default-avatar';

interface ResendRewardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (item: RewardHistoryItem) => void;
    item: RewardHistoryItem | null;
    isLoading?: boolean;
    isSuccess?: boolean;
}

const ResendRewardModal: React.FC<ResendRewardModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    item,
    isLoading = false,
    isSuccess = false
}) => {
    const { t } = useTranslation();
    // Lock body scroll when modal is open
    React.useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen || !item) return null;

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfirm = () => {
        onConfirm(item);
    };

    const modalContent = (
        <div
            className="fixed z-50 flex items-center justify-center bg-black/20"
            style={{
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                position: 'fixed'
            }}
            onClick={handleOverlayClick}
        >
            <div className="w-full max-w-[510px] h-[441px] bg-white dark:bg-gray-800 rounded-[20px] flex flex-col relative">
                {/* Header - Same for both states */}
                <div className="relative flex flex-row justify-end items-center px-[30px] pt-[25px] pb-[15px] gap-[15px] border-b border-gray-200 dark:border-gray-700 h-[86px]">
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-1/2 right-6 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 z-10 flex items-center justify-center w-6 h-6"
                        disabled={isLoading}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_x_icon)">
                                <path d="M18 6L6 18" stroke="#515967" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M6 6L18 18" stroke="#515967" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </g>
                            <defs>
                                <clipPath id="clip0_x_icon">
                                    <rect width="24" height="24" fill="white"/>
                                </clipPath>
                            </defs>
                        </svg>
                    </button>

                    {/* Icon container - Keep original Send icon */}
                    <div className="flex items-center justify-center p-[4.65px] w-[45px] h-[45px] bg-[#F2FCFA] dark:bg-emerald-900/20 rounded-[50%]">
                        <Send className="w-[25px] h-[25px] text-[#00D6A8] dark:text-emerald-400" />
                    </div>

                    {/* Text content - Keep original title */}
                    <div className="flex flex-col justify-center items-start gap-2 flex-1">
                        <h2 className="text-[23px] font-normal leading-[20px] letter-spacing-[-0.5px] text-[#0A0A0A] dark:text-white">
                            {t('reward_history.resend.title')}
                        </h2>
                        <p className="text-[12.3px] font-semibold leading-[18px] text-[#4A5565] dark:text-gray-300">
                            {t('reward_history.resend.target', { name: item.reward?.name || '' })}
                        </p>
                        {item.actions?.lastResent && (
                            <p className="text-[11px] font-normal leading-[14px] text-[#75757D] dark:text-gray-400">
                                {t('reward_history.resend.last_resent', { value: item.actions.lastResent })}
                            </p>
                        )}
                    </div>
                </div>

                {isSuccess ? (
                    // Success state content
                    <>
                        {/* Success body */}
                        <div className="flex-1 px-[30px] pt-[20px] pb-[5px] flex flex-col items-center justify-center">
                            <div className="flex flex-col items-center gap-4 text-center">
                                {/* Success message */}
                                <div className="flex flex-col items-center">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ shapeRendering: 'geometricPrecision' }}>
                                        <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="#40CB5E" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M18 24L22 28L30 20" stroke="#40CB5E" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <div className="flex flex-col items-center gap-2 mt-6">
                                        <h3 className="text-[20px] font-semibold leading-[24px] text-[#0A0A0A] dark:text-white">
                                            {t('reward_history.resend.success_title')}
                                        </h3>
                                        <p className="text-[16px] font-normal leading-[20px] text-[#75757D] dark:text-gray-300 whitespace-nowrap">
                                            {t('reward_history.resend.success_description')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Success footer */}
                        <div className="flex flex-row justify-center items-center px-[30px] py-[25px] h-[96px]">
                            <button
                                onClick={onClose}
                                className="flex items-center justify-center px-[30px] py-[8px] gap-[10px] w-full h-[46px] bg-[#00D6A8] rounded-[27px] hover:bg-[#00C29B]"
                            >
                                <span className="text-[16px] font-bold leading-[22px] text-[#FFFFFF]">
                                    {t('common.done')}
                                </span>
                            </button>
                        </div>
                    </>
                ) : (
                    // Original resend form content
                    <>
                        {/* Body */}
                        <div className="flex-1 px-[30px] pt-[20px] pb-[5px]">
                            <div className="flex flex-col gap-[10px] h-full">
                                {/* Recipient section */}
                                <div className="mb-[10px]">
                                    <h3 className="text-[15px] font-semibold leading-[20px] text-[#75757D] dark:text-gray-300 mb-[10px] mt-[10px]">
                                        {t('reward_history.resend.recipient')}
                                    </h3>

                                    {/* User info container */}
                                    <div className="flex flex-row items-center p-[15px] gap-[15px] border border-gray-200 dark:border-gray-600 rounded-[12.75px] bg-white dark:bg-gray-800">
                                        {/* Avatar */}
                                        <div className="flex flex-row justify-center items-start w-[35px] h-[35px] rounded-[50%] flex-shrink-0">
                                            <div className="w-10 h-10 bg-[#F5F5F5] dark:bg-gray-600 rounded-[50%] flex items-center justify-center">
                                                {item.user?.avatar ? (
                                                    <img src={item.user.avatar} alt={item.user?.name || ''} className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80" />
                                                ) : (
                                                    <DefaultAvatar size={40} />
                                                )}
                                            </div>
                                        </div>

                                        {/* User details */}
                                        <div className="flex flex-col gap-[3px] flex-1">
                                            <div className="text-[15px] font-semibold leading-[18px] text-[#0A0A0A] dark:text-white">
                                                {item.user?.name || ''}
                                            </div>
                                            <div className="text-[14px] font-normal leading-[14px] text-[#75757D] dark:text-gray-400">
                                                {item.user?.email || ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Warning message */}
                                <div className="flex flex-row items-start p-[15px_20px] gap-[8px] bg-[#F2FCFA] dark:bg-emerald-900/20 border border-[#00D6A8] dark:border-emerald-400 rounded-[10px]">
                                    <div className="flex-shrink-0 mt-[2px]">
                                        <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[20px] h-[21px]">
                                            <g clipPath="url(#clip0_15394_14336)">
                                                <path d="M2.5 10.5002C2.5 11.4852 2.69399 12.4604 3.0709 13.3704C3.44781 14.2803 4.00026 15.1071 4.6967 15.8035C5.39314 16.5 6.21993 17.0524 7.12987 17.4293C8.03982 17.8063 9.01509 18.0002 10 18.0002C10.9849 18.0002 11.9602 17.8063 12.8701 17.4293C13.7801 17.0524 14.6069 16.5 15.3033 15.8035C15.9997 15.1071 16.5522 14.2803 16.9291 13.3704C17.306 12.4604 17.5 11.4852 17.5 10.5002C17.5 8.51112 16.7098 6.60347 15.3033 5.19694C13.8968 3.79042 11.9891 3.00024 10 3.00024C8.01088 3.00024 6.10322 3.79042 4.6967 5.19694C3.29018 6.60347 2.5 8.51112 2.5 10.5002Z" stroke="#00D6A8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M10 8.00024H10.0083" stroke="#00D6A8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M9.16602 10.5002H9.99935V13.8336H10.8327" stroke="#00D6A8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_15394_14336">
                                                    <rect width="20" height="20" fill="white" transform="translate(0 0.500244)"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>

                                    <div className="flex-1">
                                        <p className="text-[16px] font-medium leading-[20px] text-[#75757D] dark:text-gray-300">
                                            {t('reward_history.resend.warning')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex flex-row justify-end items-center px-[30px] py-[25px] gap-[20px] h-[96px]">
                            {/* Cancel button */}
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="flex items-center justify-center px-[30px] py-[8px] gap-[10px] w-[215px] h-[46px] border border-gray-300 dark:border-gray-600 rounded-[27px] bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="text-[16px] font-bold leading-[22px] text-[#515967] dark:text-gray-300">
                                    {t('common.cancel')}
                                </span>
                            </button>

                            {/* Confirm button */}
                            <button
                                onClick={handleConfirm}
                                disabled={isLoading}
                                className="flex items-center justify-center px-[30px] py-[8px] gap-[10px] w-[215px] h-[46px] bg-[#00D6A8] dark:bg-emerald-500 rounded-[27px] hover:bg-[#00C29B] dark:hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="text-[16px] font-bold leading-[22px] text-[#004232] dark:text-white">
                                    {isLoading ? t('reward_history.resend.resending') : t('reward_history.resend.action')}
                                </span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    // Render trực tiếp vào document.body để đảm bảo overlay che phủ toàn màn hình
    return createPortal(modalContent, document.body);
};

export default ResendRewardModal;
